/**
 * useEnrollment – Per-user program enrollment
 * Source of truth: Firestore users/{uid}.enrolledPrograms (real-time)
 *
 * Rules:
 *  - Guests see NO enrollments. Enrollment requires a logged-in user.
 *  - localStorage is used ONLY as a per-user perf cache (keyed by UID).
 *  - On logout the enrolled state is immediately cleared.
 *  - On login, Firestore is the single source of truth, kept live via
 *    onSnapshot so enrollment changes from any device/browser/admin action
 *    are reflected everywhere instantly (no stale local-only state).
 *  - Unenrolling atomically removes the program AND all of its data
 *    (enrollment details, progress docs, achievements) in one batched write,
 *    except progress for subjects still shared with another active program
 *    (e.g. JEE/NEET share Physics/Chemistry) — that data is preserved.
 */
import { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, arrayUnion, deleteField, onSnapshot, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { PROGRAM_SUBJECT_KEYS } from '../config/tutorialCatalogue';

// Scope the cache key to each user so different accounts never share data.
function lsKey(uid) { return `ppx_ep_${uid}`; }

function readCache(uid) {
  try {
    const raw = localStorage.getItem(lsKey(uid));
    return raw ? JSON.parse(raw) : null; // null = not cached yet
  } catch { return null; }
}

function writeCache(uid, arr) {
  try { localStorage.setItem(lsKey(uid), JSON.stringify(arr)); } catch { /* storage full */ }
}

// Maps a program ID to all progress subjectKeys that belong to it
export { PROGRAM_SUBJECT_KEYS };

export function useEnrollment() {
  const { currentUser, loading: authLoading } = useAuth();
  // Always start empty – never pre-populate from local storage globally.
  const [enrolled, setEnrolled] = useState([]);
  const [enrollmentLoading, setEnrollmentLoading] = useState(true);

  useEffect(() => {
    // Wait until Firebase Auth has resolved before deciding what to show.
    if (authLoading) return;

    // Logged-out: clear any stale state immediately.
    if (!currentUser) {
      setEnrolled([]);
      setEnrollmentLoading(false);
      return;
    }

    // Logged-in: show cached data instantly while Firestore loads.
    const cached = readCache(currentUser.uid);
    if (cached) setEnrolled(cached);
    setEnrollmentLoading(true);

    // Real-time listener — Firestore is the single source of truth. No
    // merging with local data: any change (this tab, another device, or an
    // admin action) is reflected immediately everywhere.
    const unsub = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snap) => {
        const fsEnrolled = snap.data()?.enrolledPrograms || [];
        setEnrolled(fsEnrolled);
        writeCache(currentUser.uid, fsEnrolled);
        setEnrollmentLoading(false);
      },
      () => {
        // Firestore failed – fall back to cache so the UI isn't broken.
        if (cached) setEnrolled(cached);
        setEnrollmentLoading(false);
      }
    );

    return unsub;
  }, [currentUser?.uid, authLoading]); // eslint-disable-line

  /**
   * Enroll the current user in a program.
   * Returns false if the user is not logged in (caller should prompt login).
   */
  const enroll = useCallback(async (programId) => {
    if (!currentUser) return false;
    if (enrolled.includes(programId)) return true;
    const next = [...enrolled, programId];
    try {
      await setDoc(
        doc(db, 'users', currentUser.uid),
        { enrolledPrograms: arrayUnion(programId) },
        { merge: true }
      );
      setEnrolled(next);
      writeCache(currentUser.uid, next);
      return true;
    } catch (err) {
      console.error('[useEnrollment] enroll failed:', err?.code, err?.message);
      return false;
    }
  }, [currentUser, enrolled]);

  /**
   * Unenroll the current user from a program, deleting all of that program's
   * data atomically: enrolledPrograms entry, enrollmentDetails[programId],
   * and every progress doc for its subjects (unless another program the
   * student remains enrolled in shares that subject).
   * Returns true on success, false on failure (state is left untouched on
   * failure so the UI doesn't show a false "unenrolled" state).
   */
  const unenroll = useCallback(async (programId) => {
    if (!currentUser) return false;

    const remainingPrograms = enrolled.filter(p => p !== programId);
    // Subjects still needed by any other program the student stays enrolled in.
    const stillNeeded = new Set(
      remainingPrograms.flatMap(p => PROGRAM_SUBJECT_KEYS[p] || [])
    );
    const subjectKeysToDelete = (PROGRAM_SUBJECT_KEYS[programId] || [])
      .filter(key => !stillNeeded.has(key));

    try {
      const batch = writeBatch(db);
      batch.set(
        doc(db, 'users', currentUser.uid),
        {
          enrolledPrograms: remainingPrograms,
          [`enrollmentDetails.${programId}`]: deleteField(),
        },
        { merge: true }
      );
      subjectKeysToDelete.forEach(key => {
        batch.delete(doc(db, 'users', currentUser.uid, 'progress', key));
      });
      await batch.commit();

      // Firestore write succeeded — safe to update local state + cache now.
      setEnrolled(remainingPrograms);
      writeCache(currentUser.uid, remainingPrograms);
      subjectKeysToDelete.forEach(key => {
        try { localStorage.removeItem(`ppx_progress_${currentUser.uid}_${key}`); } catch { /* ignore */ }
      });
      return true;
    } catch (err) {
      console.error('[useEnrollment] unenroll failed:', err?.code, err?.message);
      return false;
    }
  }, [currentUser, enrolled]);

  const isEnrolled = useCallback((programId) => enrolled.includes(programId), [enrolled]);

  return { enrolled, enroll, unenroll, isEnrolled, enrollmentLoading };
}
